import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Upload, Trash2, Loader2, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const KIND_LABEL = { CONTRAT: "Contrat", AVENANT: "Avenant", AUTRE: "Autre" };

export const noticeDeadline = (endDate, months) => {
  if (!endDate || !months) return null;
  const d = new Date(`${endDate}T12:00:00`);
  d.setMonth(d.getMonth() - Number(months));
  return d.toLocaleDateString("fr-CH");
};

function UploadForm({ contractId, onUploaded }) {
  const [file, setFile] = useState(null);
  const [kind, setKind] = useState("CONTRAT");
  const [title, setTitle] = useState("");
  const [signedDate, setSignedDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [inputKey, setInputKey] = useState(0);

  const submit = async () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    fd.append("title", title);
    if (signedDate) fd.append("signed_date", signedDate);
    setBusy(true);
    try {
      await api.post(`/partner-contracts/${contractId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document enregistré");
      setFile(null); setTitle(""); setSignedDate(""); setInputKey((k) => k + 1);
      onUploaded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-2 gap-2 border-t border-[#E5E5E0] pt-3">
      <Input key={inputKey} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" className="col-span-2"
        onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="contract-doc-file" />
      <Select value={kind} onValueChange={setKind}>
        <SelectTrigger data-testid="contract-doc-kind"><SelectValue /></SelectTrigger>
        <SelectContent>
          {Object.entries(KIND_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
      <Input type="date" value={signedDate} onChange={(e) => setSignedDate(e.target.value)} title="Date de signature" data-testid="contract-doc-date" />
      <Input placeholder="Titre (ex. Avenant n°1 — nouveau taux)" value={title} onChange={(e) => setTitle(e.target.value)} className="col-span-2" data-testid="contract-doc-title" />
      <Button onClick={submit} disabled={!file || busy} className="col-span-2 bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="contract-doc-upload">
        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />} Joindre le document
      </Button>
    </div>
  );
}

export default function ContractDocuments({ contractId, confirm }) {
  const [docs, setDocs] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get(`/partner-contracts/${contractId}/documents`);
    setDocs(data || []);
  }, [contractId]);
  useEffect(() => { load().catch(() => setDocs([])); }, [load]);

  const open = async (d) => {
    try {
      const { data } = await api.get(`/contract-documents/${d.id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch { toast.error("Impossible d'ouvrir le document"); }
  };

  const remove = async (d) => {
    const ok = await confirm({ title: "Retirer ce document ?", description: d.title, confirmText: "Retirer" });
    if (!ok) return;
    await api.delete(`/contract-documents/${d.id}`);
    load();
  };

  return (
    <fieldset className="border border-[#E5E5E0] rounded-lg p-3 space-y-2" data-testid="contract-docs">
      <legend className="text-xs font-semibold text-[#1F4E3D] px-2">Documents signés</legend>
      {docs.length === 0 && <div className="text-xs text-[#575A57]">Aucun document joint.</div>}
      {docs.map((d) => (
        <div key={d.id} className="flex items-center gap-2 text-sm" data-testid={`contract-doc-${d.id}`}>
          <FileText className="w-4 h-4 text-[#1F4E3D] shrink-0" />
          <button type="button" onClick={() => open(d)} className="flex-1 text-left hover:underline truncate" data-testid={`contract-doc-open-${d.id}`}>
            <span className="text-[10px] uppercase text-[#575A57] mr-1">{KIND_LABEL[d.kind]}</span>
            {d.title}
            {d.signed_date && <span className="text-xs text-[#575A57]"> · signé le {new Date(`${d.signed_date}T12:00:00`).toLocaleDateString("fr-CH")}</span>}
            <ExternalLink className="w-3 h-3 inline ml-1" />
          </button>
          <Button variant="ghost" size="sm" onClick={() => remove(d)} data-testid={`contract-doc-delete-${d.id}`}>
            <Trash2 className="w-3 h-3 text-red-500" />
          </Button>
        </div>
      ))}
      <UploadForm contractId={contractId} onUploaded={load} />
    </fieldset>
  );
}
